'use server'

import { apiRequest, ApiResponse } from '../request'
import type { ServiceLabels } from '@/lib/form-state/util'

export type WalletDistributionMode = 'ROUND_ROBIN' | 'MANUAL' | 'LOAD_BALANCED'

export type WalletLeadCategory = 'CONSULTATION' | 'PROCEDURE' | 'EXAM' | 'SALE'

export interface SalesWalletMember {
    id: number
    walletId: number
    userId: number
    user?: {
        id: number
        name: string
        email?: string
        picture?: string
    }
    active: boolean
    createdAt: string
}

/**
 * Carteira de clientes — global por organização. O cliente pertence à
 * carteira, e todos os membros dela o atendem.
 */
export interface SalesWallet {
    id: number
    organizationId: number
    name: string
    description?: string
    /** Filiais atendidas. Vazio = todas. Governa só o roteamento. */
    companyIds: number[]
    active: boolean
    acceptsAutoAssign: boolean
    weight: number
    maxClients?: number | null
    lastAutoAssignedAt?: string | null
    members?: SalesWalletMember[]
    clientCount?: number
    createdAt: string
    updatedAt: string
    _count?: {
        members: number
        clients: number
    }
}

export interface SalesWalletConfig {
    id: number
    organizationId: number
    autoAssignEnabled: boolean
    distributionMode: WalletDistributionMode
    defaultWalletId?: number | null
    prospectDiscardEnabled: boolean
    prospectRetentionDays?: number | null
}

/** Usuário candidato à criação em massa de carteiras. */
export interface WalletEligibleUser {
    id: number
    name: string
    email?: string
    picture?: string
    companyIds: number[]
    companyNames: string[]
    /** Já é membro de alguma carteira, ou já existe carteira com o nome dele. */
    hasWallet: boolean
}

export interface CreateWalletsFromUsersData {
    userIds: number[]
    /** Vazio = cada carteira herda as filiais do próprio usuário. */
    companyIds?: number[]
    acceptsAutoAssign?: boolean
    weight?: number
    maxClients?: number | null
}

export interface CreateWalletsFromUsersResult {
    created: { walletId: number; userId: number; name: string }[]
    skipped: { userId: number; name: string; reason: string }[]
}

/** Cliente listado dentro de uma carteira. */
export interface WalletClient {
    id: number
    name: string
    email?: string | null
    whatsAppNumber?: string | null
    cpf?: string | null
    city?: string | null
    createdAt: string
}

export interface UnassignedClient {
    id: number
    name: string
    email?: string | null
    whatsAppNumber?: string | null
    createdAt: string
}

export interface CreateWalletData {
    name: string
    description?: string
    companyIds?: number[]
    acceptsAutoAssign?: boolean
    weight?: number
    maxClients?: number | null
}

export interface UpdateWalletData {
    name?: string
    description?: string
    companyIds?: number[]
    acceptsAutoAssign?: boolean
    weight?: number
    maxClients?: number | null
    active?: boolean
}

export interface AddWalletMemberData {
    userId: number
}

export interface UpdateWalletMemberData {
    active?: boolean
}

export interface UpdateWalletConfigData {
    autoAssignEnabled?: boolean
    distributionMode?: WalletDistributionMode
    defaultWalletId?: number | null
    prospectDiscardEnabled?: boolean
    prospectRetentionDays?: number | null
}

// Wallets

export async function getWallets(): Promise<ApiResponse<SalesWallet[]>> {
    return apiRequest<SalesWallet[]>('sales/wallets', {})
}

export async function getWallet(id: number): Promise<ApiResponse<SalesWallet>> {
    return apiRequest<SalesWallet>(`sales/wallets/${id}`, {})
}

export async function createWallet(
    data: CreateWalletData
): Promise<ApiResponse<SalesWallet>> {
    return apiRequest<SalesWallet>('sales/wallets', { data }, 'post')
}

export async function updateWallet(
    id: number,
    data: UpdateWalletData
): Promise<ApiResponse<SalesWallet>> {
    return apiRequest<SalesWallet>(`sales/wallets/${id}`, { data }, 'patch')
}

export async function deleteWallet(
    id: number
): Promise<ApiResponse<{ releasedClients: number }>> {
    return apiRequest<{ releasedClients: number }>(
        `sales/wallets/${id}`,
        {},
        'delete'
    )
}

export async function getEligibleUsers(): Promise<ApiResponse<WalletEligibleUser[]>> {
    return apiRequest<WalletEligibleUser[]>('sales/wallets/eligible-users', {})
}

export async function createWalletsFromUsers(
    data: CreateWalletsFromUsersData
): Promise<ApiResponse<CreateWalletsFromUsersResult>> {
    return apiRequest<CreateWalletsFromUsersResult>(
        'sales/wallets/from-users',
        { data },
        'post'
    )
}

// Wallet Members

export async function addWalletMember(
    walletId: number,
    data: AddWalletMemberData
): Promise<ApiResponse<SalesWallet>> {
    return apiRequest<SalesWallet>(
        `sales/wallets/${walletId}/members`,
        { data },
        'post'
    )
}

export async function updateWalletMember(
    walletId: number,
    userId: number,
    data: UpdateWalletMemberData
): Promise<ApiResponse<SalesWallet>> {
    return apiRequest<SalesWallet>(
        `sales/wallets/${walletId}/members/${userId}`,
        { data },
        'patch'
    )
}

export async function removeWalletMember(
    walletId: number,
    userId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `sales/wallets/${walletId}/members/${userId}`,
        {},
        'delete'
    )
}

// Clientes da carteira

/**
 * `moveDeals: false` deixa as oportunidades abertas no funil em que estão — o
 * cliente muda de carteira sem arrastar o trabalho de quem está no funil dela.
 */
export async function assignClientToWallet(
    walletId: number,
    clientId: number,
    moveDeals = true
): Promise<ApiResponse<unknown>> {
    const query = moveDeals ? '' : '?moveDeals=false'
    return apiRequest<unknown>(
        `sales/wallets/${walletId}/clients/${clientId}${query}`,
        {},
        'post'
    )
}

/** `walletId: null` devolve os clientes ao pool. Ver `assignClientToWallet`. */
export async function transferClients(
    clientIds: number[],
    walletId: number | null,
    moveDeals = true
): Promise<ApiResponse<{ moved: number }>> {
    return apiRequest<{ moved: number }>(
        'sales/wallets/transfer-clients',
        { data: { clientIds, walletId, moveDeals } },
        'post'
    )
}

export async function getUnassignedClients(
    page = 1,
    limit = 50
): Promise<ApiResponse<{ items: UnassignedClient[]; total: number }>> {
    return apiRequest<{ items: UnassignedClient[]; total: number }>(
        `sales/wallets/unassigned-clients?page=${page}&limit=${limit}`,
        {}
    )
}

export async function getWalletClients(
    walletId: number,
    params: { search?: string; page?: number; limit?: number } = {}
): Promise<ApiResponse<{ items: WalletClient[]; total: number; wallet: { id: number; name: string } }>> {
    const query = new URLSearchParams()
    if (params.search) query.set('search', params.search)
    query.set('page', String(params.page ?? 1))
    query.set('limit', String(params.limit ?? 50))

    return apiRequest<{ items: WalletClient[]; total: number; wallet: { id: number; name: string } }>(
        `sales/wallets/${walletId}/clients?${query.toString()}`,
        {}
    )
}

// Carteira ativa da sessão

export async function getActiveWallet(): Promise<
    ApiResponse<{ activeSalesWalletId: number | null }>
> {
    return apiRequest<{ activeSalesWalletId: number | null }>(
        'sales/wallets/active',
        {}
    )
}

export async function setActiveWallet(
    walletId: number | null
): Promise<ApiResponse<{ activeSalesWalletId: number | null }>> {
    return apiRequest<{ activeSalesWalletId: number | null }>(
        'sales/wallets/active',
        { data: { walletId } },
        'patch'
    )
}

// Configuração de distribuição

export async function getWalletConfig(): Promise<ApiResponse<SalesWalletConfig>> {
    return apiRequest<SalesWalletConfig>('sales/wallets/config', {})
}

export async function updateWalletConfig(
    data: UpdateWalletConfigData
): Promise<ApiResponse<SalesWalletConfig>> {
    return apiRequest<SalesWalletConfig>(
        'sales/wallets/config',
        { data },
        'patch'
    )
}

// Labels

export const WALLET_DISTRIBUTION_MODE_LABELS: Record<WalletDistributionMode, string> = {
    ROUND_ROBIN: 'Rodízio',
    MANUAL: 'Manual',
    LOAD_BALANCED: 'Balanceado por Carga',
}

export const WALLET_DISTRIBUTION_MODE_HINTS: Record<WalletDistributionMode, string> = {
    ROUND_ROBIN: 'A carteira que ficou mais tempo sem receber vem primeiro',
    LOAD_BALANCED: 'A carteira com menos clientes por peso recebe primeiro',
    MANUAL: 'Clientes sem usuário-criador ficam no pool até alguém atribuir',
}

export const WALLET_LEAD_CATEGORY_LABELS: Record<WalletLeadCategory, string> = {
    CONSULTATION: 'Consultas',
    PROCEDURE: 'Procedimentos',
    EXAM: 'Exames',
    SALE: 'Vendas',
}

/** `WALLET_LEAD_CATEGORY_LABELS` no vocabulário do segmento. */
export function getWalletLeadCategoryLabels(
    labels: ServiceLabels
): Record<WalletLeadCategory, string> {
    return {
        CONSULTATION: labels.Consultations,
        PROCEDURE: labels.Orders,
        EXAM: labels.Exams,
        SALE: 'Vendas',
    }
}
